import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { AccountsService } from '../accounts.service';
import { EInvoiceComplianceDashboard, EInvoiceComplianceDocument } from '../accounting-compliance.model';
import { SessionStorageService } from '../../services/session-storage.service';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';

@Component({
    selector: 'app-e-invoice-compliance-workbench',
    standalone: true,
    imports: [CommonModule, ButtonModule, TableModule, TagModule, ToastModule, PageShellComponent, PageHeroComponent, PageMetricStripComponent, PageDataPanelComponent],
    providers: [MessageService],
    template: `
        <p-toast></p-toast>
        <app-page-shell>
            <app-page-hero eyebrow="Compliance" title="E-Invoice Compliance Workbench"
                subtitle="Monitor ZATCA clearance/reporting for invoices, debit notes, and credit notes." icon="pi pi-shield">
                <div actions>
                    <p-button label="Refresh" icon="pi pi-refresh" (click)="load()" [loading]="loading"></p-button>
                </div>
            </app-page-hero>
            <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>
            <app-page-data-panel title="Failed Documents">
                <p-table [value]="dashboard.failedDocuments || []" [loading]="loading" styleClass="p-datatable-sm">
                    <ng-template pTemplate="header"><tr><th>Type</th><th>Number</th><th>Date</th><th>Status</th><th>Error</th><th></th></tr></ng-template>
                    <ng-template pTemplate="body" let-row>
                        <tr>
                            <td>{{ label(row.documentType) }}</td>
                            <td>{{ row.documentNumber }}</td>
                            <td>{{ row.documentDate | date:'yyyy-MM-dd' }}</td>
                            <td><p-tag [value]="row.zatcaStatus || 'FAILED'" severity="danger"></p-tag></td>
                            <td>{{ row.errorMessage || '-' }}</td>
                            <td><p-button label="Open" size="small" [text]="true" icon="pi pi-external-link" (click)="open(row)"></p-button></td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage"><tr><td colspan="6">No failed ZATCA documents.</td></tr></ng-template>
                </p-table>
            </app-page-data-panel>
            <app-page-data-panel title="Pending Documents">
                <p-table [value]="dashboard.pendingDocuments || []" [loading]="loading" styleClass="p-datatable-sm">
                    <ng-template pTemplate="header"><tr><th>Type</th><th>Number</th><th>Date</th><th>Status</th><th>Total</th><th></th></tr></ng-template>
                    <ng-template pTemplate="body" let-row>
                        <tr>
                            <td>{{ label(row.documentType) }}</td>
                            <td>{{ row.documentNumber }}</td>
                            <td>{{ row.documentDate | date:'yyyy-MM-dd' }}</td>
                            <td><p-tag [value]="row.zatcaStatus || 'PENDING'" severity="warning"></p-tag></td>
                            <td>{{ money(row.totalAmount) }}</td>
                            <td><p-button label="Open" size="small" [text]="true" icon="pi pi-external-link" (click)="open(row)"></p-button></td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage"><tr><td colspan="6">No pending ZATCA documents.</td></tr></ng-template>
                </p-table>
            </app-page-data-panel>
        </app-page-shell>
    `
})
export class EInvoiceComplianceWorkbenchComponent implements OnInit {
    dashboard: EInvoiceComplianceDashboard = {};
    loading = false;
    private companyId = 0;

    constructor(private accountsService: AccountsService, private sessionStorageService: SessionStorageService, private router: Router, private messageService: MessageService) {}

    ngOnInit(): void {
        this.companyId = this.sessionStorageService.getCompany()?.id ?? 0;
        this.load();
    }

    get metrics(): PageMetric[] {
        return [
            { label: 'Submitted', value: this.dashboard.submittedCount ?? 0, tone: 'success' },
            { label: 'Pending', value: this.dashboard.pendingCount ?? 0, tone: 'warn' },
            { label: 'Failed', value: this.dashboard.failedCount ?? 0, tone: 'danger' },
            { label: 'Not Required', value: this.dashboard.notRequiredCount ?? 0, tone: 'default' }
        ];
    }

    load(): void {
        if (!this.companyId) return;
        this.loading = true;
        this.accountsService.getEInvoiceComplianceDashboard(this.companyId).subscribe({
            next: dashboard => { this.dashboard = dashboard ?? {}; this.loading = false; },
            error: err => {
                this.loading = false;
                this.messageService.add({ severity: 'error', summary: 'Compliance dashboard failed', detail: err.error?.message || 'Could not load e-invoice compliance.' });
            }
        });
    }

    open(row: EInvoiceComplianceDocument): void {
        if (!row.documentId) return;
        if (row.documentType === 'INVOICE') this.router.navigate(['/accounts/enterprise/receivables/generated-invoices/view', row.documentId]);
        if (row.documentType === 'DEBIT_NOTE') this.router.navigate(['/accounts/enterprise/receivables/debit-note/view', row.documentId]);
        if (row.documentType === 'CREDIT_NOTE') this.router.navigate(['/accounts/enterprise/payables/credit-note/view', row.documentId]);
    }

    label(type?: string) {
        return (type || '').replace('_', ' ');
    }

    money(amount?: number) {
        return `SAR ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
}
